import { useAuth } from "@ozpanel/ui";
import type { AuthClient } from "@ozpanel/shared";

// accountsApi.ts — hand-typed wrappers over the "Accounts" nav group's real endpoints
// (tenants/domains/plans in internal/server/*.go — NOT a module.Module, so no OpenAPI-
// generated type helps here either way; apps/usage/backups ARE modules but the shapes below
// are still hand-typed against the literal Go structs, matching modules.ts's own precedent,
// since these are bespoke hand-written routes, not the generic Settings/Configurable
// machinery). Field names/shapes confirmed by reading internal/server/tenancy_handlers.go,
// tenant_users.go, plans_handlers.go, internal/modules/{appserver,usage,backup}/*.go
// directly, 2026-08-28.

// ---- Tenants ----

export interface TenantUser {
  id: number;
  email: string;
  role?: string;
  status?: string;
  mailbox?: string;
  last_login?: string;
  locked?: boolean;
}

export interface Plan {
  id: number;
  name: string;
  description?: string;
  limits?: Record<string, number> | null;
  features?: Record<string, boolean> | null;
  visible?: boolean;
  tenants?: number;
}
export interface LimitField {
  key: string;
  label: string;
  group?: string;
  unit?: string;
  countable?: boolean;
  enforcement?: string;
  detail?: string;
  /** What an unset value means for this limit (server LimitSpec.Unset); absent = no limit. */
  unset?: string;
}
export interface FeatureField {
  key: string;
  label: string;
  detail?: string;
}

export interface Tenant {
  id: number;
  name: string;
  contact_email: string;
  contact_name: string;
  plan_id?: number;
  region_policy: string;
  system_user: string;
  system_uid?: number;
  status: string;
  cluster_mode: string;
  created_at: string;
  updated_at: string;
}

export interface LimitValue {
  key: string;
  label: string;
  unit?: string;
  value: number;
  source: "plan" | "override" | "unset";
  plan?: string;
  plan_value?: number;
  unset?: string;
}

export interface FeatureValue {
  key: string;
  label: string;
  detail: string;
  on: boolean;
  source: string;
  plan?: string;
}

export interface TenantLimits {
  limits: Record<string, number>;
  effective: LimitValue[];
  overrides: Record<string, number>;
  uncapped: boolean;
  usage: Record<string, number>;
  usage_note?: string;
  features: FeatureValue[];
  /** The limit CATALOGUE — every field the platform knows, with its label, group, unit and the
   *  server's own explanation. The override form renders from this rather than a list written in
   *  the console, so a limit added server-side appears without a frontend change. */
  fields?: LimitField[] | null;
  groups?: string[] | null;
}

// ---- Apps (appserver) ----

export interface AppNodeView {
  node: string;
  state: string; // running|stopped|failed|fenced|stray|unknown
  candidate: boolean;
  leaseholder?: boolean;
  running: boolean;
  present: boolean;
  status?: string;
  uptime?: string;
  note?: string;
  ip?: string; // the container's address on that node (per-node; the route targets it)
}

export interface AppRuntime {
  this_node: string;
  nodes: AppNodeView[];
  running: number;
  expected: number;
  unknown: number;
  summary: string;
  healthy: boolean;
}

// /apps/{name}/inspect. Docker's default bridge hands out the same address on every node, so
// every runtime field here is only true for the node named in measured_on.
export interface AppInspect {
  name: string;
  measured_on?: string;
  image?: string;
  image_digest?: string;
  unit?: string;
  port?: number;
  domain?: string;
  runtime?: string;
  state?: { running?: boolean; id?: string; ip?: string; exit_code?: number; restart_count?: number; local?: boolean; measured_on?: string };
  resources?: { cpu_pct?: string | number; memory?: string; memory_pct?: number; net_io?: string; block_io?: string; pids?: number; size_rw_mb?: number };
  network?: { ip?: string; name?: string };
  placement?: { kind?: string; ha_mode?: string; nodes?: string[]; targets?: string[]; enforced?: boolean; cluster_mode?: string };
  routing?: { caddy_upstream?: string; live_upstream?: string; drift?: boolean; drift_detail?: string; local?: boolean; live_id?: string; measured_on?: string };
  live?: { running?: number; expected?: number; unknown?: number; healthy?: boolean; summary?: string; this_node?: string; nodes?: unknown[] };
  limits?: { memory_mb?: number; scratch_mb?: number };
  volumes?: { backing?: string; path?: string; source?: string }[] | null;
}

export interface Backend {
  id: number;
  tenant_id: number;
  tenant_name?: string;
  domain: string;
  name: string;
  command: string;
  working_dir: string;
  port: number;
  enabled: boolean;
  active: boolean;
  unit: string;
  live?: AppRuntime;
  runtime: string; // process | container
  image: string;
  ha_mode: string; // singleton | replicated — "" for a process app (no placement concept)
  nodes: string[] | null; // null for a process app — confirmed against a real response, not []
  mem_max_mb: number;
  scratch_mb: number;
}

// ---- Usage ----

export interface UsageCurrent {
  tenant_id: number;
  tenant?: string;
  metrics: Record<string, number>;
  nodes?: string[];
  measured_at?: string;
  stale: boolean;
}

// ---- Backups ----

export interface BackupTarget {
  id: number;
  name: string;
  kind: string; // local | sftp
  destination: string;
  offsite: boolean;
  created_at: string;
  warning?: string;
  // sftp targets
  port?: number;
  auth?: "key" | "password";
  /** The public key to install in the remote user's authorized_keys (key auth). */
  public_key?: string;
  /** untested until Test connection passes; failed shows the reason in warning. */
  connection?: "untested" | "ok" | "failed";
  tested_at?: string;
}

export interface BackupSchedule {
  id: number;
  target_id: number;
  target: string;
  what: string;
  cron: string;
  keep: number;
  active: boolean;
  last_run_at: string | null;
  warning?: string;
}

export interface BackupSnapshot {
  id: number;
  name: string;
  status: string;
  what: string;
  bytes: number;
  parts: number;
  started_at: string;
  finished_at?: string;
  detail?: string;
  /** The node whose disk holds the files — backups stay where they were taken. */
  node?: string;
  held_by?: string;
  /** Measured by the holding node: available | missing | unknown (that node is not reporting). */
  availability?: "available" | "missing" | "unknown";
  /** Present only when measured; absent when the holder could not be asked. */
  on_disk?: boolean;
  note?: string;
  warning?: string;
}

/** One node's JuiceFS metadata dumps for one volume, as that node measured its own disk. */
export interface JuiceFSMetaItem {
  node: string;
  volume: string;
  count: number;
  bytes: number;
  newest: string;
  newest_bytes: number;
  oldest: string;
  last_proven?: string;
  damaged: number;
  availability: "available" | "unknown";
  note?: string;
  path: string;
  /** Newest first — what a restore can pick from (the archive stays on that node). */
  archives?: string[];
}

export interface BackupStatusItem {
  tenant_id: number;
  tenant: string;
  system_user: string;
  snapshots: number;
  failed: number;
  partial: number;
  schedules: number;
  last_success: string | null;
  age?: string;
}

/** TeardownPreview is what GET /tenants/{id}/teardown reports a delete WOULD remove — shown
 *  verbatim in the delete confirmation rather than a generic "are you sure". */
export interface TeardownPreview {
  tenant?: string;
  removes?: string[];
  note?: string;
  [k: string]: unknown;
}

export function useAccountsApi() {
  const { client } = useAuth();
  return buildApi(client);
}

function buildApi(client: AuthClient) {
  const app = (name: string) => `/apps/${encodeURIComponent(name)}`;
  return {
    createTenant: (body: Record<string, unknown>) =>
      client.apiFetch<{ id: number }>("/tenants", { method: "POST", body: JSON.stringify(body) }),
    listDomains: () =>
      client.apiFetch<{ items: { id: number; fqdn: string; tenant_id: number }[] }>("/domains"),
    // The plan list ships the CATALOGUES with it — `fields` is every limit the platform knows,
    // `features` every capability, each with a label and an explanation the server wrote. The
    // editor renders from those rather than from a hardcoded list, so a limit added server-side
    // appears in the form without a frontend change, and a limit that is removed stops being
    // offered instead of silently writing a key nothing reads.
    listPlans: () =>
      client.apiFetch<{
        items: Plan[];
        fields?: LimitField[] | null;
        features?: FeatureField[] | null;
        groups?: string[] | null;
      }>("/plans"),
    // Returns the whole plan, not just an id — verified against node10 rather than assumed.
    createPlan: (body: Record<string, unknown>) =>
      client.apiFetch<Plan>("/plans", { method: "POST", body: JSON.stringify(body) }),
    updatePlan: (id: number, body: Record<string, unknown>) =>
      client.apiFetch<unknown>(`/plans/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    // Refused by the server while accounts are on it — the refusal is surfaced rather than
    // pre-empted here, because the console's idea of who is on a plan can be stale and the
    // server's cannot.
    deletePlan: (id: number) => client.apiFetch<unknown>(`/plans/${id}`, { method: "DELETE" }),
    createDomain: (body: Record<string, unknown>) =>
      client.apiFetch<unknown>("/domains", { method: "POST", body: JSON.stringify(body) }),
    createApp: (body: Record<string, unknown>) =>
      client.apiFetch<unknown>("/apps", { method: "POST", body: JSON.stringify(body) }),
    listPlacementNodes: () =>
      client.apiFetch<{ nodes: string[]; this_node: string }>("/apps/placement/nodes"),
    createBackupTarget: (tenantId: number, body: Record<string, unknown>) =>
      client.apiFetch<unknown>(`/backups/targets?tenant=${tenantId}`, { method: "POST", body: JSON.stringify(body) }),
    testBackupTarget: (tenantId: number, id: number) =>
      client.apiFetch<{ ok: boolean; detail: string }>(`/backups/targets/${id}/test?tenant=${tenantId}`, { method: "POST" }),
    createBackupSchedule: (tenantId: number, body: Record<string, unknown>) =>
      client.apiFetch<unknown>(`/backups/schedules?tenant=${tenantId}`, { method: "POST", body: JSON.stringify(body) }),
    listTenants: () => client.apiFetch<{ items: Tenant[]; total: number }>("/tenants"),

    // Tenant LOGINS. The console could list an account and not give it a way in: create, role
    // change, password reset, invitation and removal were reached by nothing.
    listTenantUsers: (id: number) =>
      client.apiFetch<{ items: TenantUser[] | null; total?: number }>(`/tenants/${id}/users`),
    createTenantUser: (id: number, body: Record<string, unknown>) =>
      client.apiFetch<unknown>(`/tenants/${id}/users`, { method: "POST", body: JSON.stringify(body) }),
    // PARTIAL: only the fields sent are changed. Sending the whole object would turn every unset
    // field into an assertion and quietly re-apply stale values.
    updateTenantUser: (id: number, uid: number, body: Record<string, unknown>) =>
      client.apiFetch<unknown>(`/tenants/${id}/users/${uid}`, { method: "PUT", body: JSON.stringify(body) }),
    deleteTenantUser: (id: number, uid: number) =>
      client.apiFetch<unknown>(`/tenants/${id}/users/${uid}`, { method: "DELETE" }),
    // Omitting the password makes the SERVER generate one and return it once — which is better
    // than a console inventing one, because the server's is the only copy that was never in a
    // form field or a browser's memory.
    resetTenantUserPassword: (id: number, uid: number, password?: string) =>
      client.apiFetch<{ password?: string }>(`/tenants/${id}/users/${uid}/password`, {
        method: "POST",
        body: JSON.stringify(password ? { password } : {}),
      }),
    inviteTenantUser: (id: number, uid: number) =>
      client.apiFetch<unknown>(`/tenants/${id}/users/${uid}/invite`, { method: "POST" }),

    // The PROVISIONING lifecycle — activate, suspend, unsuspend, cancel. None of these was
    // reached by any console, so v2 could not suspend a non-paying customer or restore one.
    provisionSuspend: (domain: string, reason: string) =>
      client.apiFetch<unknown>("/provision/suspend", {
        method: "POST",
        body: JSON.stringify(reason ? { domain, reason } : { domain }),
      }),
    provisionUnsuspend: (domain: string) =>
      client.apiFetch<unknown>("/provision/unsuspend", { method: "POST", body: JSON.stringify({ domain }) }),
    // `delete` defaults to false on the server for a reason worth repeating at the call site: a
    // cancellation that destroys mail the moment an invoice is late has no undo, and billing
    // systems send this on a schedule. Erasure has to be asked for in the same call that says so.
    provisionCancel: (domain: string, erase: boolean) =>
      client.apiFetch<unknown>("/provision/cancel", {
        method: "POST",
        body: JSON.stringify({ domain, delete: erase }),
      }),
    getTenant: (id: number) => client.apiFetch<Tenant>(`/tenants/${id}`),
    getTenantLimits: (id: number) => client.apiFetch<TenantLimits>(`/tenants/${id}/limits`),
    // SPARSE by design: a key that is absent falls back to the plan. Sending every field would
    // convert the whole set into overrides and the account would stop following its plan.
    setTenantLimits: (id: number, limits: Record<string, number>) =>
      client.apiFetch<unknown>(`/tenants/${id}/limits`, { method: "PUT", body: JSON.stringify({ limits }) }),
    // 0 removes the plan, which is why it is sent as a number rather than an id-or-null.
    setTenantPlan: (id: number, plan_id: number) =>
      client.apiFetch<unknown>(`/tenants/${id}/plan`, { method: "PUT", body: JSON.stringify({ plan_id }) }),
    // A PARTIAL map: omitting a key returns that capability to whatever the plan says. Sending the
    // whole set with every value pinned would silently convert every plan-derived feature into an
    // override, and the account would stop following its plan without anyone choosing that.
    setTenantFeatures: (id: number, features: Record<string, boolean>) =>
      client.apiFetch<unknown>(`/tenants/${id}/features`, { method: "PUT", body: JSON.stringify({ features }) }),
    setTenantClustering: (id: number, mode: string, targets: string[]) =>
      client.apiFetch<unknown>(`/tenants/${id}/clustering`, { method: "PUT", body: JSON.stringify({ mode, targets }) }),
    suspendTenant: (id: number) => client.apiFetch<void>(`/tenants/${id}/suspend`, { method: "POST" }),
    unsuspendTenant: (id: number) => client.apiFetch<void>(`/tenants/${id}/unsuspend`, { method: "POST" }),
    // GET, not a mutation — read ahead of the delete confirmation so the dialog can show what
    // the server itself says will be removed.
    teardownPreview: (id: number) => client.apiFetch<TeardownPreview>(`/tenants/${id}/teardown`),
    deleteTenant: (id: number) => client.apiFetch<void>(`/tenants/${id}`, { method: "DELETE" }),
    impersonateTenant: (id: number) =>
      client.apiFetch<{ note?: string }>(`/tenants/${id}/impersonate`, { method: "POST" }),

    listApps: () => client.apiFetch<{ apps: Backend[]; this_node: string }>("/apps"),
    // The MEASURED view, as opposed to the stored record getApp returns: live container state,
    // resources, volumes with their backing store, and routing drift. `measured_on` names which
    // node answered, which matters because runtime state is per-node.
    inspectApp: (name: string) => client.apiFetch<AppInspect>(`/apps/${encodeURIComponent(name)}/inspect`),
    appLogs: (name: string, lines = 200) =>
      client.apiFetch<{ logs: string; unit?: string; from_node?: string; nodes?: { node: string; ok: boolean; local: boolean; active: boolean }[] }>(
        `/apps/${encodeURIComponent(name)}/logs?lines=${lines}`,
      ),
    // Whole-object PUT is not usable for a move: it demands image, command, port and working dir,
    // and reconstructing an app's full spec from memory mid-incident is the last thing an operator
    // can do accurately. This endpoint takes the placement alone.
    setAppPlacement: (name: string, nodes: string[], ha_mode: string) =>
      client.apiFetch<{ running_here?: boolean; note?: string }>(`/apps/${encodeURIComponent(name)}/placement`, {
        method: "POST",
        body: JSON.stringify({ nodes, ha_mode }),
      }),

    getApp: (name: string) => client.apiFetch<Backend>(app(name)),
    startApp: (name: string) => client.apiFetch<void>(`${app(name)}/start`, { method: "POST" }),
    stopApp: (name: string) => client.apiFetch<void>(`${app(name)}/stop`, { method: "POST" }),
    restartApp: (name: string) => client.apiFetch<void>(`${app(name)}/restart`, { method: "POST" }),
    // enable/disable are DESIRED STATE and journalled cluster-wide; start/stop are transient
    // on the placement node. Different things, and the UI must not conflate them.
    enableApp: (name: string) => client.apiFetch<void>(`${app(name)}/enable`, { method: "POST" }),
    disableApp: (name: string) => client.apiFetch<void>(`${app(name)}/disable`, { method: "POST" }),
    relinkApp: (name: string) => client.apiFetch<{ note?: string }>(`${app(name)}/relink`, { method: "POST" }),
    deleteApp: (name: string) => client.apiFetch<void>(app(name), { method: "DELETE" }),

    usageOverview: () =>
      client.apiFetch<{ items: UsageCurrent[]; total: number; metrics: string[] }>("/stats/overview"),

    // A super_admin must name a tenant for every backups sub-resource EXCEPT /backups/status
    // (the fleet-wide rollup) — confirmed against a real 400 ("name a tenant with
    // ?tenant=<id>") from backup.go's own tenantOf(). A domain_admin is forced to their own
    // tenant server-side either way, so passing tenantId when known is always correct.
    backupStatus: () => client.apiFetch<{ items: BackupStatusItem[]; total: number; unprotected: string[] }>(
      "/backups/status",
    ),
    backupTargets: (tenantId: number) =>
      client.apiFetch<{ items: BackupTarget[]; total: number }>(`/backups/targets?tenant=${tenantId}`),
    backupSchedules: (tenantId: number) =>
      client.apiFetch<{ items: BackupSchedule[]; total: number }>(`/backups/schedules?tenant=${tenantId}`),
    juicefsMeta: () =>
      client.apiFetch<{ items: JuiceFSMetaItem[]; total: number; note?: string }>("/backups/juicefs-meta"),
    restorePlatform: (body: { node: string; archive: string; paths: string[]; dry_run: boolean }) =>
      client.apiFetch<{ node: string; entries: number; sample?: string[]; safety_archive?: string; note?: string; dry_run: boolean }>(
        "/backups/platform/restore",
        { method: "POST", body: JSON.stringify(body) },
      ),
    platformBackups: () =>
      client.apiFetch<{ items: JuiceFSMetaItem[]; total: number; note?: string }>("/backups/platform"),
    backupSnapshots: (tenantId: number) =>
      client.apiFetch<{ items: BackupSnapshot[]; total: number }>(`/backups/snapshots?tenant=${tenantId}`),
    runBackup: (tenantId: number) =>
      client.apiFetch<{ snapshot: string; bytes: number }>(`/backups/run?tenant=${tenantId}`, { method: "POST" }),
    deleteBackupTarget: (tenantId: number, id: number) =>
      client.apiFetch<void>(`/backups/targets/${id}?tenant=${tenantId}`, { method: "DELETE" }),
    setScheduleActive: (tenantId: number, id: number, active: boolean) =>
      client.apiFetch<void>(`/backups/schedules/${id}?tenant=${tenantId}`, {
        method: "PUT",
        body: JSON.stringify({ active }),
      }),
    deleteBackupSchedule: (tenantId: number, id: number) =>
      client.apiFetch<void>(`/backups/schedules/${id}?tenant=${tenantId}`, { method: "DELETE" }),
    verifySnapshot: (tenantId: number, id: number) =>
      client.apiFetch<{ verified: boolean; parts_checked: number; problems?: string[]; node?: string }>(
        `/backups/snapshots/${id}/verify?tenant=${tenantId}`,
        { method: "POST" },
      ),
    // dry_run defaults true here on purpose: a restore overwrites live data, and the server's
    // own dry-run report is what the operator should see before the real thing.
    restoreSnapshot: (tenantId: number, id: number, dryRun: boolean) =>
      client.apiFetch<{ done?: string[]; skipped?: string[]; note?: string }>(
        `/backups/snapshots/${id}/restore?tenant=${tenantId}`,
        { method: "POST", body: JSON.stringify({ files: true, databases: true, dry_run: dryRun }) },
      ),
    deleteSnapshot: (tenantId: number, id: number) =>
      client.apiFetch<void>(`/backups/snapshots/${id}?tenant=${tenantId}`, { method: "DELETE" }),
  };
}
